import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Center,
  Flex,
  Image,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  HStack,
  Divider,
  Avatar,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import dominus_logo_transparent_white from '../assets/dominus_logo_transparent_white.png';
import apiClient from '../lib/api/apiClient';
import VisitorFooter from '../components/VisitorFooter';
import { useAuthStore } from '../store/authStore';

interface InviteData {
  requested_roles: string[];
  inviter_name?: string;
  message?: string;
  email_address?: string;
  team: {
    name: string;
    id: number;
  };
  league: {
    name: string;
    id: number;
  };
}

interface TeamMember {
  avatar: string;
  display_name: string;
  roles: Array<{
    role_id: string;
    role_name: string;
  }>;
  user_id: string;
}

const InviteLandingPage = () => {
  const navigate = useNavigate();
  const { shortCode } = useParams<{ shortCode: string }>();
  const { isSignedIn } = useAuthStore();
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [declineMessage, setDeclineMessage] = useState<string>('');
  const [isDeclining, setIsDeclining] = useState<boolean>(false);
  const { isOpen: isDeclineOpen, onOpen: onDeclineOpen, onClose: onDeclineClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    const fetchInviteData = async () => {
      if (!shortCode) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/team/invite-user-info/${shortCode}`);
        console.log(response.data);
        setInviteData(response.data.invite_request);
        setTeamMembers(response.data.members);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching invite data:', err);
        setError(
          err?.response?.data?.message || 
          'Failed to load invite information. This invite may have expired or is invalid.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInviteData();
  }, [shortCode]);

  const handleAcceptInvite = async () => {
    if (!shortCode) return;

    try {
      await apiClient.post(`/team/accept-invitation/${shortCode}`);
      localStorage.removeItem('pendingInvite');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      toast({
        title: 'Error',
        description: err?.response?.data?.message || 'Failed to accept invitation. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Debug modal state
  useEffect(() => {
    console.log('isDeclineOpen changed:', isDeclineOpen);
  }, [isDeclineOpen]);

  const handleDeclineInvitation = async () => {
    if (!shortCode) return;

    try {
      setIsDeclining(true);
      const payload: any = {};
      if (declineMessage.trim()) {
        payload.decline_reason = declineMessage.trim();
      }

      await apiClient.post(`/team/decline-invitation/${shortCode}`, payload);

      toast({
        title: 'Invitation Declined',
        description: 'You have successfully declined the invitation.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onDeclineClose();
      setDeclineMessage('');
      navigate('/');
    } catch (err: any) {
      console.error('Error declining invitation:', err);
      toast({
        title: 'Error',
        description: err?.response?.data?.message || 'Failed to decline invitation. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeclining(false);
    }
  };

  // If user is already signed in, show them they can accept the invite
  if (isSignedIn && inviteData) {
    return (
      <Box
        minH="100vh"
        w="100%"
        display="flex"
        flexDirection="column"
        position="absolute"
        top="0"
        left="0"
        bgGradient="linear(to-b, black, #002e0b)"
        color="white"
        fontFamily="'Exo 2', sans-serif"
      >
        <Box
          as="nav"
          position="fixed"
          top={0}
          w="100%"
          bg="transparent"
          backdropFilter="blur(8px)"
          zIndex={50}
        >
          <Flex justifyContent="space-between" maxW="8xl" mx="auto" px={4} py={4}>
            <Box flexShrink={0}>
              <Image src={dominus_logo_transparent_white} alt="Dominus" h={12} />
            </Box>
            <Box>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="dominus"
              >
                Go to Dashboard
              </Button>
            </Box>
          </Flex>
        </Box>

        <Container maxW="800px" pt={32} flex="1 0 auto">
          <Center minH="60vh">
            <VStack gap={8} width="full">
              <Box
                bg="black"
                p={8}
                rounded="lg"
                w="full"
                boxShadow="xl"
                borderWidth={1}
                borderColor="green.500"
              >
                <VStack gap={6} align="stretch">
                  <VStack gap={2} align="center">
                    <Heading
                      as="h1"
                      size="xl"
                      fontFamily="Audiowide"
                      fontWeight="bold"
                      color="white"
                      textAlign="center"
                    >
                      Team Invitation
                    </Heading>
                    <Text color="gray.300" fontSize="lg" textAlign="center">
                      You're already signed in!
                    </Text>
                  </VStack>

                  <Divider borderColor="gray.700" />

                  <VStack gap={4} align="stretch">
                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={1}>
                        Team
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="white">
                        {inviteData.team.name}
                      </Text>
                    </Box>

                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={1}>
                        League
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="white">
                        {inviteData.league.name}
                      </Text>
                    </Box>

                    <Box>
                      <Text color="gray.400" fontSize="sm" mb={2}>
                        Invited Role(s)
                      </Text>
                      <HStack flexWrap="wrap" gap={2} alignItems="center" justifyContent="center">
                        {inviteData.requested_roles.map((role, index) => (
                          <Badge
                            key={index}
                            colorScheme="green"
                            fontSize="md"
                            px={3}
                            py={1}
                            borderRadius="md"
                          >
                            {role}
                          </Badge>
                        ))}
                      </HStack>
                    </Box>

                    {inviteData.inviter_name && (
                      <Box>
                        <Text color="gray.400" fontSize="sm" mb={1}>
                          Invited By
                        </Text>
                        <Text fontSize="lg" color="white">
                          {inviteData.inviter_name}
                        </Text>
                      </Box>
                    )}

                    {inviteData.message && (
                      <Box
                        bg="gray.900"
                        p={4}
                        borderRadius="md"
                        borderLeft="4px solid"
                        borderColor="green.500"
                      >
                        <Text color="gray.400" fontSize="sm" mb={1}>
                          Personal Message
                        </Text>
                        <Text color="white" fontSize="md" whiteSpace="pre-wrap">
                          {inviteData.message}
                        </Text>
                      </Box>
                    )}

                    {teamMembers.length > 0 && (
                      <Box>
                        <Text color="gray.400" fontSize="sm" mb={3}>
                          Current Team Members
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                          {teamMembers.map((member) => (
                            <Box
                              key={member.user_id}
                              bg="gray.900"
                              p={3}
                              borderRadius="md"
                              borderWidth={1}
                              borderColor="gray.700"
                            >
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  src={member.avatar ? `/api/${member.avatar}` : undefined}
                                  name={member.display_name}
                                  bg="green.500"
                                />
                                <VStack align="start" spacing={1} flex={1}>
                                  <Text color="white" fontSize="sm" fontWeight="medium">
                                    {member.display_name}
                                  </Text>
                                  <HStack spacing={1} flexWrap="wrap">
                                    {member.roles.map((role, index) => (
                                      <Badge
                                        key={index}
                                        colorScheme="green"
                                        fontSize="xs"
                                        px={2}
                                        py={0.5}
                                        borderRadius="sm"
                                      >
                                        {role.role_name}
                                      </Badge>
                                    ))}
                                  </HStack>
                                </VStack>
                              </HStack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </Box>
                    )}
                  </VStack>

                  <Divider borderColor="gray.700" />

                  <VStack gap={3}>
                    <Button
                      onClick={() => handleAcceptInvite()}
                      variant="dominus"
                      size="md"
                    >
                      Accept Invitation & Go to Dashboard
                    </Button>
                    <HStack spacing={3} justify="center">
                      <Button
                        onClick={() => {
                          console.log('Decline button clicked, isDeclineOpen:', isDeclineOpen);
                          onDeclineOpen();
                        }}
                        variant="outline"
                        colorScheme="red"
                        size="md"
                      >
                        Decline Invitation
                      </Button>
                      <Button
                        onClick={() => navigate('/dashboard')}
                        variant="outline"
                        colorScheme="green"
                        size="md"
                      >
                        Go to Dashboard
                      </Button>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            </VStack>
          </Center>
        </Container>
        <VisitorFooter />
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      w="100%"
      display="flex"
      flexDirection="column"
      position="absolute"
      top="0"
      left="0"
      bgGradient="linear(to-b, black, #002e0b)"
      color="white"
      fontFamily="'Exo 2', sans-serif"
    >
      {/* Navigation Bar */}
      <Box
        as="nav"
        position="fixed"
        top={0}
        w="100%"
        bg="transparent"
        backdropFilter="blur(8px)"
        zIndex={50}
      >
        <Flex justifyContent="space-between" maxW="8xl" mx="auto" px={4} py={4}>
          <Box flexShrink={0}>
            <Image src={dominus_logo_transparent_white} alt="Dominus" h={12} />
          </Box>
          <Box>
            <Button
              onClick={() => navigate('/signin')}
              variant="dominus"
            >
              Sign In
            </Button>
          </Box>
        </Flex>
      </Box>

      {/* Invite Content */}
      <Container maxW="800px" pt={32} flex="1 0 auto">
        <Center minH="60vh">
          <VStack gap={8} width="full">
            {loading ? (
              <Box
                bg="black"
                p={12}
                rounded="lg"
                w="full"
                textAlign="center"
                boxShadow="xl"
              >
                <VStack gap={4}>
                  <Spinner size="xl" color="green.500" thickness="4px" />
                  <Text fontSize="lg" color="gray.300">
                    Loading invitation details...
                  </Text>
                </VStack>
              </Box>
            ) : error ? (
              <Box
                bg="black"
                p={8}
                rounded="lg"
                w="full"
                boxShadow="xl"
              >
                <VStack gap={6}>
                  <Alert
                    status="error"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    height="auto"
                    py={6}
                    borderRadius="md"
                    bg="red.900"
                    color="white"
                  >
                    <AlertIcon boxSize="40px" mr={0} color="red.300" />
                    <AlertTitle mt={4} mb={1} fontSize="lg">
                      Invitation Not Found
                    </AlertTitle>
                    <AlertDescription maxWidth="sm" fontSize="md">
                      {error}
                    </AlertDescription>
                  </Alert>
                  <Button
                    onClick={() => navigate('/')}
                    variant="dominus"
                    size="lg"
                    w="full"
                  >
                    Go to Home Page
                  </Button>
                </VStack>
              </Box>
            ) : inviteData ? (
              <VStack gap={6} w="full">
                <VStack gap={3} textAlign="center" w="full">
                  <Heading
                    as="h1"
                    size="2xl"
                    fontFamily="Audiowide"
                    fontWeight="bold"
                    color="white"
                  >
                    You're Invited!
                  </Heading>
                  <Text fontSize="xl" color="gray.300">
                    Join <strong>{inviteData.team.name}</strong> on Dominus Baseball
                  </Text>
                </VStack>

                <Box
                  bg="black"
                  p={8}
                  rounded="lg"
                  w="full"
                  boxShadow="xl"
                  borderWidth={1}
                  borderColor="green.500"
                >
                  <VStack gap={6} align="stretch">
                    <VStack gap={4} align="stretch">
                      <Box>
                        <Text color="gray.400" fontSize="sm" mb={1}>
                          Team
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color="white">
                          {inviteData.team.name}
                        </Text>
                      </Box>

                      <Box>
                        <Text color="gray.400" fontSize="sm" mb={1}>
                          League
                        </Text>
                        <Text fontSize="xl" color="white">
                          {inviteData.league.name}
                        </Text>
                      </Box>

                      <Box>
                        <Text color="gray.400" fontSize="sm" mb={2}>
                          Invited Role(s)
                        </Text>
                        <HStack flexWrap="wrap" gap={2} alignItems="center" justifyContent="center">
                          {inviteData.requested_roles.map((role, index) => (
                            <Badge
                              key={index}
                              colorScheme="green"
                              fontSize="md"
                              px={3}
                              py={1}
                              borderRadius="md"
                            >
                              {role}
                            </Badge>
                          ))}
                        </HStack>
                      </Box>

                      {inviteData.inviter_name && (
                        <Box>
                          <Text color="gray.400" fontSize="sm" mb={1}>
                            Invited By
                          </Text>
                          <Text fontSize="lg" color="white">
                            {inviteData.inviter_name}
                          </Text>
                        </Box>
                      )}

                      {inviteData.message && (
                        <Box
                          bg="gray.900"
                          p={4}
                          borderRadius="md"
                          borderLeft="4px solid"
                          borderColor="green.500"
                        >
                          <Text color="gray.400" fontSize="sm" mb={1}>
                            Personal Message
                          </Text>
                          <Text color="white" fontSize="md" whiteSpace="pre-wrap">
                            {inviteData.message}
                          </Text>
                        </Box>
                      )}

                      {teamMembers.length > 0 && (
                        <Box>
                          <Text color="gray.400" fontSize="sm" mb={3}>
                            Current Team Members
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            {teamMembers.map((member) => (
                              <Box
                                key={member.user_id}
                                bg="gray.900"
                                p={3}
                                borderRadius="md"
                                borderWidth={1}
                                borderColor="gray.700"
                              >
                                <HStack spacing={3}>
                                  <Avatar
                                    size="sm"
                                    src={member.avatar ? `/api/${member.avatar}` : undefined}
                                    name={member.display_name}
                                    bg="green.500"
                                  />
                                  <VStack align="start" spacing={1} flex={1}>
                                    <Text color="white" fontSize="sm" fontWeight="medium">
                                      {member.display_name}
                                    </Text>
                                    <HStack spacing={1} flexWrap="wrap">
                                      {member.roles.map((role, index) => (
                                        <Badge
                                          key={index}
                                          colorScheme="green"
                                          fontSize="xs"
                                          px={2}
                                          py={0.5}
                                          borderRadius="sm"
                                        >
                                          {role.role_name}
                                        </Badge>
                                      ))}
                                    </HStack>
                                  </VStack>
                                </HStack>
                              </Box>
                            ))}
                          </SimpleGrid>
                        </Box>
                      )}
                    </VStack>

                    <Divider borderColor="gray.700" />

                    <VStack gap={3}>
                      <Text fontSize="lg" color="gray.300" textAlign="center">
                        Create an account or sign in to accept this invitation
                      </Text>
                      <Button
                        onClick={() => {
                          // Store the invite short code so we can accept after signup
                          localStorage.setItem('pendingInvite', shortCode || '');
                          navigate('/signup');
                        }}
                        variant="dominus"
                        size="md"
                      >
                        Create Account
                      </Button>
                      <Text color="gray.400" fontSize="sm">
                        Already have an account?
                      </Text>
                      <HStack spacing={3} justify="center">
                        <Button
                          onClick={() => {
                            console.log('Decline button clicked (unauthenticated), isDeclineOpen:', isDeclineOpen);
                            onDeclineOpen();
                          }}
                          variant="outline"
                          colorScheme="red"
                          size="md"
                        >
                          Decline Invitation
                        </Button>
                        <Button
                          onClick={() => {
                            // Store the invite short code so we can accept after signin
                            localStorage.setItem('pendingInvite', shortCode || '');
                            navigate('/signin');
                          }}
                          variant="outline"
                          colorScheme="green"
                          size="md"
                        >
                          Sign In
                        </Button>
                      </HStack>
                    </VStack>
                  </VStack>
                </Box>
              </VStack>
            ) : null}
          </VStack>
        </Center>
      </Container>
      <VisitorFooter />

      {/* Decline Invitation Modal */}
      <Modal isOpen={isDeclineOpen} onClose={onDeclineClose} size="sm">
        <ModalOverlay />
        <ModalContent bg="black" borderWidth={1} borderColor="red.500">
          <ModalHeader color="white" pb={2} fontSize="md">
            Decline Invitation
          </ModalHeader>
          <ModalCloseButton color="white" size="sm" />
          
          <ModalBody pb={3}>
            <VStack spacing={3} align="stretch">
              <Text color="gray.300" fontSize="xs">
                Are you sure you want to decline this invitation? You can optionally provide a reason.
              </Text>
              
              <FormControl>
                <FormLabel color="gray.400" fontSize="xs" mb={1}>
                  Reason (optional)
                </FormLabel>
                <Textarea
                  value={declineMessage}
                  onChange={(e) => setDeclineMessage(e.target.value)}
                  placeholder="Enter your reason for declining..."
                  size="xs"
                  rows={2}
                  resize="vertical"
                  bg="gray.900"
                  borderColor="gray.600"
                  color="white"
                  fontSize="sm"
                  _placeholder={{ color: 'gray.500', fontSize: 'sm' }}
                  _focus={{
                    borderColor: 'red.500',
                    boxShadow: '0 0 0 1px red.500'
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter pt={2}>
            <HStack spacing={2}>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={onDeclineClose}
                isDisabled={isDeclining}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeclineInvitation}
                isLoading={isDeclining}
                loadingText="Declining..."
                size="sm"
              >
                Decline
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InviteLandingPage;

